import React from 'react';
import {
  titleUI,
  fileInputMultipleSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import environment from 'platform/utilities/environment';
import { useSelector } from 'react-redux';
import { getFormData } from 'platform/forms-system/src/js/state/selectors';
import { caveMultiFileUploader } from '../components/caveMultiFileUploader';
import { createCaveUploadReview } from '../components/CaveUploadReview';
import { CAVE_VALIDATION_FIELD } from '../domain/artifacts';

const UploadMessage = (
  <p>
    <strong>Note:</strong> You can submit your supporting documents and
    additional evidence after submitting your application. You’ll need to submit
    them by mail or upload them using the Claim Status Tool.
  </p>
);

const filesUi = caveMultiFileUploader({
  title: 'Select a file to upload',
  hint: 'You can upload a .pdf, .jpg, .jpeg, or .png file. Your file should be no larger than 50 MB (non-PDF) or 99 MB (PDF only).',
  required: false,
  fileUploadUrl: `${environment.API_URL}/v0/claim_attachments`,
  maxFileSize: 103809024, // 99 MB for PDFs
  accept: '.pdf,.jpg,.jpeg,.png',
  formNumber: '21P-534EZ',
  disallowEncryptedPdfs: true,
});

/**
 * Blocks the Continue button while CAVE is still processing uploaded documents.
 * Rendered as a hidden `view:caveProcessing` field so ui:validations can gate
 * form navigation without displaying anything when processing is complete.
 */
const CaveProcessingField = () => {
  const formData = useSelector(getFormData) || {};
  const IN_FLIGHT_STATUSES = new Set([
    'pending',
    'processing',
    'fetching-artifacts',
    'downloading-artifacts',
  ]);
  const isProcessing = (formData.files ?? []).some(f =>
    IN_FLIGHT_STATUSES.has(f.idpUploadStatus),
  );

  if (!isProcessing) return null;

  return (
    <va-loading-indicator
      label="Processing your documents"
      message="We’re extracting information from your documents. This may take a few minutes. Once processing is complete, you’ll be able to continue."
      set-focus
    />
  );
};

// Renders nothing. Exists only so `view:caveArtifacts` is present in this page's
// uiSchema, which lets reduceErrors/getPropertyInfo map the submit-time
// incomplete-artifact error to this chapter.
const CaveArtifactsValidationField = () => null;

export default {
  uiSchema: {
    ...titleUI(
      'Submit your supporting documents',
      'You can submit your supporting documents and additional evidence with your application.',
    ),
    files: filesUi,
    'view:uploadMessage': {
      'ui:description': UploadMessage,
    },
    'view:caveProcessing': {
      'ui:field': CaveProcessingField,
      'ui:validations': [
        (_errors, _fieldValue, formData) => {
          const IN_FLIGHT_STATUSES = new Set([
            'pending',
            'processing',
            'fetching-artifacts',
            'downloading-artifacts',
          ]);
          const processing = (formData?.files ?? []).some(f =>
            IN_FLIGHT_STATUSES.has(f.idpUploadStatus),
          );
          if (processing) _errors.addError('Documents are still processing.');
        },
      ],
    },
    [CAVE_VALIDATION_FIELD]: {
      'ui:field': CaveArtifactsValidationField,
    },
  },
  schema: {
    type: 'object',
    properties: {
      files: fileInputMultipleSchema(),
      'view:uploadMessage': {
        type: 'object',
        properties: {},
      },
      'view:caveProcessing': {
        type: 'object',
        properties: {},
      },
      [CAVE_VALIDATION_FIELD]: {
        type: 'object',
        properties: {},
      },
    },
  },
  CustomPageReview: createCaveUploadReview({
    uploadPath: '/additional-information/upload-documents-cave',
    title: 'Supporting documents',
  }),
};
